import { useEffect, useState } from 'react'
import { useDrillableIslands, useStore } from './state/store'
import type { Island } from './types'
import { dueIslands, readiness } from './lib/analysis'
import { Dashboard } from './views/Dashboard'
import { IslandsView } from './views/IslandsView'
import { IslandView } from './views/IslandView'
import { IslandEditor } from './views/IslandEditor'
import { CreateWithAI } from './views/CreateWithAI'
import { ListenDrill } from './views/ListenDrill'
import { RecallDrill } from './views/RecallDrill'
import { PerformDrill } from './views/PerformDrill'
import { AmbushDrill } from './views/AmbushDrill'
import { ScenariosView } from './views/ScenariosView'
import { ScenarioRun } from './views/ScenarioRun'
import { SettingsView } from './views/SettingsView'
import { HelpView } from './views/HelpView'
import { RefresherView } from './views/RefresherView'
import { GrammarView } from './views/GrammarView'
import { SignInView } from './views/SignInView'
import { UsersView } from './views/UsersView'
import { AcceptInviteView } from './views/AcceptInviteView'
import { ResetPasswordView } from './views/ResetPasswordView'
import { SubscribeView } from './views/SubscribeView'
import { WelcomeView } from './views/WelcomeView'
import { useAccess } from './state/useAccess'
import { SyncBadge } from './components/SyncBadge'
import { TrialBanner } from './components/TrialBanner'
import { SpeechSpeed } from './components/SpeechSpeed'
import { LevelPicker } from './components/LevelPicker'
import { Colophon } from './components/Colophon'
import { Logo } from './components/Logo'
import {
  LANGUAGE_LIST,
  languageFromHost,
  languageOf,
  urlForLanguage,
  type LanguageCode,
} from './lib/languages'
import { useSync } from './state/useSync'
import { grammarFor, splitGrammar } from './data/grammar'
import { levelFor } from './lib/levels'

export type Route =
  | { view: 'dashboard' }
  | { view: 'islands' }
  | { view: 'create' }
  | { view: 'island'; id: string }
  /*
   * `draft` carries an island that does not exist in the store yet.
   *
   * "Build an island" used to write a blank island BEFORE showing the form, so
   * Cancel cancelled nothing: it left a "New island" with one empty block in
   * your list and dropped you on its empty detail page. Handing the editor an
   * unsaved draft instead means Save is the only thing that ever writes.
   */
  | { view: 'edit'; id: string; draft?: Island }
  | { view: 'listen'; id: string }
  | { view: 'recall'; id: string }
  | { view: 'perform'; id: string }
  | { view: 'ambush' }
  | { view: 'scenarios' }
  | { view: 'help' }
  | { view: 'grammar' }
  | { view: 'scenario'; id: string }
  | { view: 'settings' }
  | { view: 'refresher' }
  | { view: 'signin' }
  | { view: 'users' }

export type Navigate = (route: Route) => void

/**
 * Invitations arrive as `/?invite=<token>`. A query parameter rather than a
 * path, because this is a single-page app with no server-side routing — a real
 * path would depend on Pages' unmatched-route behaviour, which is not something
 * an invitation link should rest on.
 */
function inviteTokenFromUrl(): string | null {
  if (typeof window === 'undefined') return null
  return new URLSearchParams(window.location.search).get('invite')
}

/**
 * Reset links arrive as `/#reset=<token>`, in the fragment rather than the query
 * string. A fragment is never sent to a server, so a live credential does not
 * end up in Cloudflare's request logs, a proxy's, or anybody's analytics.
 */
function resetTokenFromUrl(): string | null {
  if (typeof window === 'undefined') return null
  const hash = window.location.hash.replace(/^#/, '')
  return new URLSearchParams(hash).get('reset')
}

/**
 * Whether this browser has already said "look around first".
 *
 * Per browser rather than per account, necessarily: the state exists precisely
 * for someone who has no account. Kept out of the synced store for the same
 * reason.
 */
const LOOKED_AROUND_KEY = 'language-island:looked-around'

function hasLookedAround(): boolean {
  try {
    return localStorage.getItem(LOOKED_AROUND_KEY) === 'yes'
  } catch {
    return false
  }
}

/**
 * What Stripe sent us back with.
 *
 * `?billing=done` and `?billing=cancelled` are the return legs of hosted
 * checkout, and until now nothing read either — two comments in the billing
 * code claimed the app "reads these on the way back to say what happened" and
 * no code did. Someone paid, was returned here, and the page said nothing at
 * all, which reads exactly like a failed payment.
 *
 * `?billing=subscribe` is the trial-reminder email's "Keep it" button, which
 * otherwise landed on the dashboard and left the person to find the
 * subscription card themselves.
 */
function billingOutcomeFromUrl(): 'done' | 'cancelled' | 'subscribe' | null {
  if (typeof window === 'undefined') return null
  const value = new URLSearchParams(window.location.search).get('billing')
  return value === 'done' || value === 'cancelled' || value === 'subscribe' ? value : null
}

export function App() {
  const { data } = useStore()
  const [inviteToken, setInviteToken] = useState<string | null>(inviteTokenFromUrl)
  const [resetToken, setResetToken] = useState<string | null>(resetTokenFromUrl)
  const [lookedAround, setLookedAround] = useState(hasLookedAround)
  const [billing] = useState(billingOutcomeFromUrl)
  const [route, setRoute] = useState<Route>(
    // Straight to the subscription card when that is what the link was for.
    billingOutcomeFromUrl() ? { view: 'settings' } : { view: 'dashboard' },
  )
  const [focused, setFocused] = useState(false)
  const sync = useSync()
  const { dispatch } = useStore()
  const active = data.settings.activeLanguage
  // On a language subdomain the host is the source of truth; the switcher
  // becomes navigation between sites rather than a filter within one.
  const hostLanguage = languageFromHost()
  const ui = languageOf(active).ui
  const access = useAccess()
  // Only the languages this account pays for. Signed out there is nothing to
  // show; on the apex, before the check returns, show nothing rather than
  // flashing five and then removing four.
  // Signed in, the pills are what you pay for. Signed out you are browsing the
  // shop window, so every language is on offer.
  const available = access.signedIn
    ? LANGUAGE_LIST.filter((entry) => access.languages.includes(entry.code))
    : LANGUAGE_LIST

  // The rail, the drills and the dashboard all show one language at a time.
  // Mixing them would make "due today" meaningless and drop German blocks into
  // a Mandarin ambush. And within the language, the level: the rail lists what
  // the islands screen lists, or the two contradict each other on screen.
  const drillable = useDrillableIslands()
  const scenarios = data.scenarios.filter((s) => s.language === active)
  const grammar = grammarFor(active)
  const grammarAtLevel = splitGrammar(grammar, levelFor(data.settings, active)).ready.length

  // Leaving a drill must never strand the app in stripped-back pressure mode.
  useEffect(() => {
    setFocused(false)
  }, [route])

  /*
   * Take the parameter out of the address bar once it has been read.
   *
   * Otherwise a refresh — or a bookmark made on the way back from Stripe —
   * announces a payment that happened once, days ago, every time it is opened.
   * replaceState rather than assigning to location, so this does not reload.
   */
  // The landing screen's retry counter is per payment, not per browser.
  useEffect(() => {
    if (billing === 'done' && access.entitledHere) {
      try {
        sessionStorage.removeItem('billing-retries')
      } catch {
        // Private browsing. The counter simply starts from zero next time.
      }
    }
  }, [billing, access.entitledHere])

  useEffect(() => {
    if (!billing) return
    try {
      const url = new URL(window.location.href)
      url.searchParams.delete('billing')
      window.history.replaceState({}, '', url.toString())
    } catch {
      // A stubbed location in tests, or an exotic browser. Nothing depends on it.
    }
  }, [billing])

  useEffect(() => {
    document.documentElement.dataset.reducedMotion = String(data.settings.reducedMotion)
  }, [data.settings.reducedMotion])

  // The tab should say which language site this is, not always Mandarin.
  useEffect(() => {
    const lang = languageOf(active)
    document.title = `${lang.ui.brand} — ${lang.name}`
    document.documentElement.lang = lang.code
  }, [active])

  const navigate: Navigate = (next) => setRoute(next)
  // Only what this learner can actually do today — see useDrillableIslands.
  const due = dueIslands(drillable).length

  const isIslandRoute =
    route.view === 'islands' ||
    route.view === 'island' ||
    route.view === 'edit' ||
    route.view === 'listen' ||
    route.view === 'recall' ||
    route.view === 'perform'

  if (resetToken) {
    return (
      <div className="shell is-bare">
        <main className="main">
          <div className="main-narrow">
            <ResetPasswordView
              token={resetToken}
              onDone={() => {
                // Drop the token from the address bar, so a back-button press or
                // a shared screenshot cannot replay it.
                window.history.replaceState({}, '', window.location.pathname)
                setResetToken(null)
                setRoute({ view: 'signin' })
              }}
            />
          </div>
        </main>
      </div>
    )
  }

  if (inviteToken) {
    return (
      <div className="shell is-bare">
        <main className="main">
          <div className="main-narrow">
            <AcceptInviteView
              token={inviteToken}
              onDone={() => {
                // Drop the token from the address bar so a shared screenshot or
                // a back-button press cannot replay it.
                window.history.replaceState({}, '', window.location.pathname)
                setInviteToken(null)
                setRoute({ view: 'dashboard' })
              }}
            />
          </div>
        </main>
      </div>
    )
  }

  /*
   * The front door, for someone with no account.
   *
   * This condition did not exist: the paywall below required signedIn, so an
   * un-signed-in visitor fell through to the full app with nothing offering an
   * account — and no screen in the codebase called signUp at all. Anyone
   * arriving from "Start seven days free" landed in a working product with no
   * way to buy it.
   *
   * Shown once per browser. "Look around first" is remembered, because being
   * asked again on every visit is its own kind of broken.
   */
  if (hostLanguage && !access.signedIn && !access.loading && !lookedAround) {
    return (
      <div className="shell is-bare">
        <main className="main">
          <div className="main-narrow">
            <WelcomeView
              language={hostLanguage}
              onSignIn={() => {
                setLookedAround(true)
                setRoute({ view: 'signin' })
              }}
              onLookAround={() => {
                try {
                  localStorage.setItem(LOOKED_AROUND_KEY, 'yes')
                } catch {
                  // A browser refusing storage still gets past this screen.
                }
                setLookedAround(true)
              }}
            />
          </div>
        </main>
      </div>
    )
  }

  /*
   * Paid, and back here before the webhook landed.
   *
   * Stripe redirects the browser the instant the card clears, while the grant
   * arrives separately over the webhook — usually within a second, occasionally
   * not. Falling through to the paywall in that window shows somebody who has
   * just been charged a page headed "Mandarin is not on your account", which is
   * the single worst thing this app could say to a new customer.
   */
  if (billing === 'done' && hostLanguage && access.signedIn && !access.loading && !access.entitledHere) {
    return (
      <div className="shell is-bare">
        <main className="main">
          <div className="main-narrow">
            <PaymentLanding language={hostLanguage} />
          </div>
        </main>
      </div>
    )
  }

  if (hostLanguage && access.signedIn && !access.loading && !access.entitledHere) {
    return (
      <div className="shell is-bare">
        <main className="main">
          <div className="main-narrow">
            <SubscribeView
              language={hostLanguage}
              held={access.languages}
              trialAvailable={access.trialAvailable}
            />
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className={`shell ${focused ? 'is-focused' : ''}`}>
      <nav className="rail" aria-label="Sections">
        <div className="brand">
          <Logo size={34} brand={ui.brand} />
        </div>

        {available.length > 1 && (
          <div className="lang-switch" role="group" aria-label="Language">
            {available.map((entry) => (
              <button
                key={entry.code}
                type="button"
                className={`lang-chip ${entry.code === active ? 'is-on' : ''}`}
                onClick={() => {
                  if (hostLanguage) {
                    // Each language has its own site; go there rather than
                    // pretending to switch inside this one.
                    window.location.href = urlForLanguage(entry.code, window.location.hostname)
                    return
                  }
                  dispatch({ type: 'updateSettings', patch: { activeLanguage: entry.code } })
                  navigate({ view: 'dashboard' })
                }}
                title={entry.name}
                aria-label={`Switch to ${entry.name}`}
              >
                {entry.nativeName}
              </button>
            ))}
          </div>
        )}

        {!sync.signedIn && (
          <div className="rail-group">
            <SyncBadge
              phase={sync.phase}
              pendingCount={sync.pendingCount}
              lastSyncAt={sync.lastSyncAt}
              signedIn={false}
              accountEmail={null}
              onSignIn={() => navigate({ view: 'signin' })}
              onOpenSettings={() => navigate({ view: 'settings' })}
              onSignOut={() => {}}
            />
          </div>
        )}

        <SpeechSpeed />
        <LevelPicker />

        <div className="rail-group">
          <RailLink
            active={route.view === 'dashboard'}
            onClick={() => navigate({ view: 'dashboard' })}
            label="Today"
            zh={ui.today}
            note={due > 0 ? `${due} due` : undefined}
          />
          <RailLink
            active={isIslandRoute}
            onClick={() => navigate({ view: 'islands' })}
            label="Islands"
            zh={ui.islands}
            /*
              The count at this level, not the whole library.
              
              This said `islands.length` — every island in the language,
              unmoved by the level control. So the Islands screen showed
              nineteen while the rail insisted on thirty-eight, on the same
              screen at the same moment, and stepping the level changed the
              number in one place and not the other. The app contradicting
              itself reads as the level doing nothing at all.
            */
            note={String(drillable.length)}
          />
          <RailLink
            active={route.view === 'scenarios' || route.view === 'scenario'}
            onClick={() => navigate({ view: 'scenarios' })}
            label="Scenarios"
            zh={ui.scenarios}
            note={String(scenarios.length)}
          />
          <RailLink
            active={route.view === 'ambush'}
            onClick={() => navigate({ view: 'ambush' })}
            label="Ambush"
            zh={ui.ambush}
          />
          {/* Mandarin only: the content is Mandarin. A rail link that opens an
              empty screen in German is worse than no link at all. */}
          {active === 'zh' && (
            <RailLink
              active={route.view === 'refresher'}
              onClick={() => navigate({ view: 'refresher' })}
              label="Refresher"
              zh="常用词"
            />
          )}
          {/*
            Only where there is grammar to show, for the reason the Refresher
            gives above — but gated on the language, not the level: rules that
            are all above you are still one press away on that screen. The
            count is the rules at this level, not the whole list; the islands
            badge taught that one.
          */}
          {grammar.length > 0 && (
            <RailLink
              active={route.view === 'grammar'}
              onClick={() => navigate({ view: 'grammar' })}
              label="Grammar"
              zh={ui.grammar}
              note={String(grammarAtLevel)}
            />
          )}
          {sync.signedIn && (
            <RailLink
              active={route.view === 'users'}
              onClick={() => navigate({ view: 'users' })}
              label="Accounts"
              zh={ui.accounts}
            />
          )}
          <RailLink
            active={route.view === 'help'}
            onClick={() => navigate({ view: 'help' })}
            label="Help"
            zh={ui.help}
          />
          <RailLink
            active={route.view === 'settings'}
            onClick={() => navigate({ view: 'settings' })}
            label="Settings"
            zh={ui.settings}
          />
        </div>

        <div className="rail-group" hidden={!sync.signedIn}>
          <SyncBadge
            phase={sync.phase}
            pendingCount={sync.pendingCount}
            lastSyncAt={sync.lastSyncAt}
            signedIn={sync.signedIn}
            accountEmail={data.sync.accountEmail}
            onSignIn={() => navigate({ view: 'signin' })}
            onOpenSettings={() => navigate({ view: 'settings' })}
            onSignOut={() => {
              const pending = sync.pendingCount
              const ok = window.confirm(
                pending > 0
                  ? `Sign out with ${pending} change${pending === 1 ? '' : 's'} not yet sent? They stay on this device and go up when you sign back in.`
                  : 'Sign out? Your islands stay on this device.',
              )
              if (ok) dispatch({ type: 'signOut' })
            }}
          />
        </div>

        <div className="rail-group">
          <div className="rail-label">Readiness</div>
          {/*
            The same set the islands screen lists, for the same reason as the
            badge above: two lists of "your islands" that disagree about what
            is in them is worse than either. Anything above the level is one
            press away on the islands screen, where the count is on the button.
          */}
          {drillable.length === 0 && <div className="rail-label muted">No islands yet</div>}
          {drillable.map((island) => {
            const state = readiness(island)
            return (
              <button
                key={island.id}
                type="button"
                className={`rail-link ${
                  'id' in route && route.id === island.id ? 'is-active' : ''
                }`}
                onClick={() => navigate({ view: 'island', id: island.id })}
              >
                <span className="rail-link-zh">{island.titleZh}</span>
                <span className={`badge is-${state}`}>{state}</span>
              </button>
            )
          })}
        </div>
      </nav>

      <main className="main">
        <div className="main-narrow">
          {/* Above the screen rather than inside one, so it is present wherever
              you happen to be — the countdown is a fact about the account, not
              about the dashboard.

              Two exceptions. Focus mode, because interrupting a timed drill to
              sell something would be indefensible. And Settings, which already
              carries the full subscription card — stacking an identical "Keep
              Mandarin" button above it is just asking the same question
              twice. */}
          {!focused && route.view !== 'settings' && <TrialBanner standing={access.standing} />}
          <Screen
            route={route}
            navigate={navigate}
            setFocused={setFocused}
            standing={access.standing}
            billing={billing}
          />
          {/* Not during a drill: the shell already strips the rail in focus
              mode so nothing competes with the countdown, and an attribution
              line under it would put something back. */}
          {!focused && <Colophon />}
        </div>
      </main>
    </div>
  )
}

function RailLink({
  active,
  onClick,
  label,
  zh,
  note,
}: {
  active: boolean
  onClick: () => void
  label: string
  zh: string
  note?: string
}) {
  return (
    <button type="button" className={`rail-link ${active ? 'is-active' : ''}`} onClick={onClick}>
      <span>
        <span className="rail-link-zh">{zh}</span>{' '}
        <span style={{ fontSize: '0.82rem' }}>{label}</span>
      </span>
      {note && <span className="badge">{note}</span>}
    </button>
  )
}

/**
 * The gap between "card accepted" and "access granted".
 *
 * Deliberately reloads itself rather than polling silently: the whole app —
 * rail, language pills, entitlement — is built from the status fetched at mount,
 * so a fresh load is what actually reveals the new subscription. Twice, a few
 * seconds apart, then it stops and leaves the reader a button; a page that
 * retries for ever is worse than one that admits it is stuck.
 */
function PaymentLanding({ language }: { language: LanguageCode }) {
  const lang = languageOf(language)
  const [waited, setWaited] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => {
      const tries = Number(sessionStorage.getItem('billing-retries') ?? '0')
      if (tries >= 2) {
        setWaited(true)
        return
      }
      sessionStorage.setItem('billing-retries', String(tries + 1))
      window.location.reload()
    }, 2500)
    return () => clearTimeout(timer)
  }, [])

  return (
    <div className="signin">
      <h1 className="page-title" style={{ marginBottom: '0.3rem' }}>
        Thank you — setting up {lang.name}
      </h1>
      <p className="lede">
        Your payment went through. The subscription takes a moment to reach this
        side; nothing further is needed from you.
      </p>
      {waited && (
        <div className="card">
          <p className="english" style={{ marginTop: 0 }}>
            It is taking longer than usual. Your payment is safe — Stripe has it, and the
            subscription will appear here on its own. If it has not within a few minutes, write to
            hello@language-island.com and quote the address you paid with.
          </p>
          <button type="button" className="btn is-primary" onClick={() => window.location.reload()}>
            Check again
          </button>
        </div>
      )}
    </div>
  )
}

function Screen({
  route,
  navigate,
  setFocused,
  standing,
  billing,
}: {
  route: Route
  navigate: Navigate
  setFocused: (v: boolean) => void
  /** How this site's language is held, for the subscription card in Settings. */
  standing: { source: string; expiresAt: number | null } | null
  /** What Stripe sent us back with, so the card can acknowledge it. */
  billing: 'done' | 'cancelled' | 'subscribe' | null
}) {
  switch (route.view) {
    case 'dashboard':
      return <Dashboard navigate={navigate} />
    case 'islands':
      return <IslandsView navigate={navigate} />
    case 'island':
      return <IslandView id={route.id} navigate={navigate} />
    case 'create':
      return <CreateWithAI navigate={navigate} />
    case 'edit':
      return <IslandEditor id={route.id} draft={route.draft} navigate={navigate} />
    case 'listen':
      return <ListenDrill id={route.id} navigate={navigate} />
    case 'recall':
      return <RecallDrill id={route.id} navigate={navigate} />
    case 'perform':
      return <PerformDrill id={route.id} navigate={navigate} setFocused={setFocused} />
    case 'ambush':
      return <AmbushDrill navigate={navigate} setFocused={setFocused} />
    case 'scenarios':
      return <ScenariosView navigate={navigate} />
    case 'scenario':
      return <ScenarioRun id={route.id} navigate={navigate} setFocused={setFocused} />
    case 'help':
      return <HelpView navigate={navigate} />
    case 'grammar':
      return <GrammarView navigate={navigate} />

    case 'settings':
      return <SettingsView navigate={navigate} standing={standing} billing={billing} />
    case 'refresher':
      return <RefresherView navigate={navigate} />
    case 'signin':
      return <SignInView onDone={() => navigate({ view: 'dashboard' })} />
    case 'users':
      return <UsersView />
    default:
      return null
  }
}
